import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, getImageUrl } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { calcularNivelLiderazgo } from '../utils/nivelLiderazgo';
import DetalleEvaluacionModal from './DetalleEvaluacionModal';
import './AdminPanel.css';

const POR_PAGINA = 25;
// Tope por llamada que impone PostgREST; el CSV pide de a este tamaño hasta traer todo.
const LOTE_CSV = 1000;

const COMPETENCIAS_CSV = [
  ['innovacion', 'Innovación'],
  ['inteligencia_emocional', 'Inteligencia Emocional'],
  ['humor', 'Humor'],
  ['ego', 'Ego'],
  ['proposito', 'Propósito'],
  ['adaptabilidad', 'Adaptabilidad'],
  ['empatia', 'Empatía'],
  ['motivacion', 'Motivación'],
  ['negociacion', 'Negociación'],
  ['trabajo_equipo', 'Trabajo en Equipo'],
  ['factor_contexto', 'Factor de Contexto'],
];

// Orden inicial de cada vista: lo más reciente primero.
const ORDEN_INICIAL = {
  usuarios: { campo: 'registrado_en', asc: false },
  completadas: { campo: 'fecha', asc: false },
  inconclusas: { campo: 'fecha', asc: false },
};

export const ETIQUETAS_ROL = {
  usuario: 'Usuario',
  admin: 'Admin',
  superadmin: 'Superadmin',
};

export const formatearFecha = (fecha) => {
  if (!fecha) return '—';
  return new Date(fecha).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

// Confirma y aplica el cambio usuario <-> admin. Devuelve true si se aplicó.
export const alternarAdmin = async (usuario) => {
  const nuevoRol = usuario.rol === 'admin' ? 'usuario' : 'admin';
  const mensaje = nuevoRol === 'admin'
    ? `¿Dar acceso de administrador a ${usuario.nombre || usuario.email}?`
    : `¿Quitar el acceso de administrador a ${usuario.nombre || usuario.email}?`;
  if (!window.confirm(mensaje)) return false;

  try {
    await adminAPI.cambiarRol(usuario.id, nuevoRol);
    return true;
  } catch (error) {
    console.error('Error al cambiar rol:', error);
    window.alert(`No se pudo cambiar el rol: ${error.message}`);
    return false;
  }
};

// Borrar un usuario no se puede deshacer: se pide escribir su email para confirmar.
export const eliminarUsuario = async (usuario) => {
  const escrito = window.prompt(
    `Vas a eliminar a ${usuario.nombre || usuario.email} junto con su cuenta y TODAS sus evaluaciones. `
    + 'Esto no se puede deshacer.\n\nPara confirmar, escribe su email:'
  );
  if (escrito == null) return false;
  if (escrito.trim().toLowerCase() !== (usuario.email || '').toLowerCase()) {
    window.alert('El email no coincide. No se eliminó nada.');
    return false;
  }

  try {
    await adminAPI.eliminarUsuario(usuario.id);
    return true;
  } catch (error) {
    console.error('Error al eliminar usuario:', error);
    window.alert(`No se pudo eliminar el usuario: ${error.message}`);
    return false;
  }
};

export const eliminarEvaluacion = async (evaluacion, nombre) => {
  const fecha = formatearFecha(evaluacion.fecha_evaluacion);
  if (!window.confirm(`¿Eliminar la evaluación de ${nombre || 'este usuario'} del ${fecha}? Esto no se puede deshacer.`)) {
    return false;
  }

  try {
    await adminAPI.eliminarEvaluacion(evaluacion.id);
    return true;
  } catch (error) {
    console.error('Error al eliminar evaluación:', error);
    window.alert(`No se pudo eliminar la evaluación: ${error.message}`);
    return false;
  }
};

// Botón de acción solo con ícono; el texto sale como tooltip al pasar el mouse
// y como aria-label para lectores de pantalla.
export const BotonIcono = ({ icono, texto, onClick, peligro = false }) => (
  <button
    type="button"
    className={`btn-accion btn-icono ${peligro ? 'btn-icono-peligro' : ''}`}
    onClick={onClick}
    aria-label={texto}
    data-tooltip={texto}
  >
    {icono}
  </button>
);

export const iconoRol = (rol) => (rol === 'admin'
  ? { icono: '🚫', texto: 'Quitar admin' }
  : { icono: '🛡️', texto: 'Hacer admin' });

const escaparCSV = (valor) => {
  const texto = valor == null ? '' : String(valor);
  return /[",\n;]/.test(texto) ? `"${texto.replace(/"/g, '""')}"` : texto;
};

const descargarCSV = (encabezados, filas, nombre) => {
  const csv = [encabezados, ...filas].map((fila) => fila.map(escaparCSV).join(',')).join('\n');
  // BOM para que Excel respete tildes y eñes
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${nombre}_${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

const PersonaCelda = ({ imagen, nombre, email }) => (
  <div className="admin-usuario-celda">
    <div
      className="admin-avatar"
      style={{ backgroundImage: imagen ? `url(${getImageUrl(imagen)})` : 'none' }}
    >
      {!imagen && (nombre || email || '?').charAt(0).toUpperCase()}
    </div>
    <div>
      <div className="admin-usuario-nombre">{nombre || 'Sin nombre'}</div>
      <div className="admin-usuario-email">{email}</div>
    </div>
  </div>
);

// Encabezado clickeable: primer clic ordena de menor a mayor, el segundo al revés.
const ThOrdenable = ({ campo, orden, onOrdenar, children }) => {
  const activo = orden.campo === campo;
  return (
    <th
      className={`th-ordenable ${activo ? 'activo' : ''}`}
      onClick={() => onOrdenar(campo)}
      aria-sort={activo ? (orden.asc ? 'ascending' : 'descending') : 'none'}
    >
      {children}
      <span className="th-flecha">{activo ? (orden.asc ? '▲' : '▼') : '↕'}</span>
    </th>
  );
};

const Paginacion = ({ pagina, total, onCambiar }) => {
  const totalPaginas = Math.max(1, Math.ceil(total / POR_PAGINA));
  const desde = total === 0 ? 0 : (pagina - 1) * POR_PAGINA + 1;
  const hasta = Math.min(pagina * POR_PAGINA, total);
  return (
    <div className="admin-paginacion">
      <span className="admin-paginacion-info">Mostrando {desde}–{hasta} de {total}</span>
      <div className="admin-paginacion-botones">
        <button className="btn-volver" onClick={() => onCambiar(1)} disabled={pagina <= 1} aria-label="Primera página">«</button>
        <button className="btn-volver" onClick={() => onCambiar(pagina - 1)} disabled={pagina <= 1}>Anterior</button>
        <span className="admin-paginacion-actual">Página {pagina} de {totalPaginas}</span>
        <button className="btn-volver" onClick={() => onCambiar(pagina + 1)} disabled={pagina >= totalPaginas}>Siguiente</button>
        <button className="btn-volver" onClick={() => onCambiar(totalPaginas)} disabled={pagina >= totalPaginas} aria-label="Última página">»</button>
      </div>
    </div>
  );
};

const AdminPanel = () => {
  const { usuario: usuarioAuth, esSuperadmin } = useAuth();
  const navigate = useNavigate();

  const [resumen, setResumen] = useState({ registrados: 0, completadas: 0, inconclusas: 0 });
  // 'usuarios' | 'completadas' | 'inconclusas': lo eligen las tarjetas de arriba.
  const [vista, setVista] = useState('usuarios');
  const [busqueda, setBusqueda] = useState('');
  const [busquedaAplicada, setBusquedaAplicada] = useState('');
  const [filtroRol, setFiltroRol] = useState('todos');
  const [orden, setOrden] = useState(ORDEN_INICIAL.usuarios);
  const [pagina, setPagina] = useState(1);

  const [filas, setFilas] = useState([]);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [exportando, setExportando] = useState(false);
  const [evaluacionSeleccionada, setEvaluacionSeleccionada] = useState(null);
  const ultimaPeticion = useRef(0);

  const vistaEvaluaciones = vista !== 'usuarios';

  // Espera a que se deje de escribir para no pedir una página por tecla.
  useEffect(() => {
    const t = setTimeout(() => {
      setBusquedaAplicada(busqueda);
      setPagina(1);
    }, 300);
    return () => clearTimeout(t);
  }, [busqueda]);

  const cargarResumen = () => {
    adminAPI.resumen()
      .then(({ data }) => data && setResumen(data))
      .catch((err) => console.error('Error al cargar resumen:', err));
  };

  useEffect(() => {
    cargarResumen();
  }, []);

  const parametros = (extra = {}) => ({
    busqueda: busquedaAplicada,
    rol: filtroRol === 'todos' ? null : filtroRol,
    orden: orden.campo,
    asc: orden.asc,
    ...(vistaEvaluaciones ? { completada: vista === 'completadas' } : {}),
    ...extra,
  });

  const pedir = (params) => (vistaEvaluaciones
    ? adminAPI.listarEvaluaciones(params)
    : adminAPI.listarUsuarios(params));

  const cargarPagina = async () => {
    const id = ++ultimaPeticion.current;
    setCargando(true);
    try {
      const respuesta = await pedir(parametros({ limit: POR_PAGINA, offset: (pagina - 1) * POR_PAGINA }));
      // Si llegó una respuesta vieja (ya se cambió de filtro u orden), se descarta.
      if (id !== ultimaPeticion.current) return;
      setFilas(respuesta.data || []);
      setTotal(respuesta.total);
      setError(null);
    } catch (err) {
      if (id !== ultimaPeticion.current) return;
      console.error('Error al cargar datos:', err);
      setError('No se pudieron cargar los datos.');
    } finally {
      if (id === ultimaPeticion.current) setCargando(false);
    }
  };

  useEffect(() => {
    cargarPagina();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vista, busquedaAplicada, filtroRol, orden, pagina]);

  const cambiarVista = (nuevaVista) => {
    // Al cambiar de vista las filas viejas no sirven (otra forma), se vacían.
    if (nuevaVista !== vista) setFilas([]);
    setVista(nuevaVista);
    setOrden(ORDEN_INICIAL[nuevaVista]);
    setPagina(1);
  };

  const verTodos = () => {
    cambiarVista('usuarios');
    setFiltroRol('todos');
    setBusqueda('');
  };

  // Clic en una tarjeta de evaluaciones ya activa vuelve a la lista de usuarios.
  const alternarVista = (nuevaVista) => {
    cambiarVista(vista === nuevaVista ? 'usuarios' : nuevaVista);
  };

  const ordenarPor = (campo) => {
    setOrden((actual) => (actual.campo === campo ? { campo, asc: !actual.asc } : { campo, asc: true }));
    setPagina(1);
  };

  const onAlternarAdmin = async (e, u) => {
    e.stopPropagation();
    if (await alternarAdmin(u)) cargarPagina();
  };

  // Tras borrar, si la página quedó vacía se retrocede una (el efecto recarga);
  // si no, se recarga la misma.
  const recargarTrasEliminar = () => {
    cargarResumen();
    if (filas.length === 1 && pagina > 1) setPagina(pagina - 1);
    else cargarPagina();
  };

  const onEliminarUsuario = async (e, u) => {
    e.stopPropagation();
    if (await eliminarUsuario(u)) recargarTrasEliminar();
  };

  const onEliminarEvaluacion = async (evaluacion, nombre) => {
    if (await eliminarEvaluacion(evaluacion, nombre)) recargarTrasEliminar();
  };

  const exportarCSV = async () => {
    setExportando(true);
    try {
      const todas = [];
      for (let offset = 0; ; offset += LOTE_CSV) {
        const { data } = await pedir(parametros({ limit: LOTE_CSV, offset }));
        todas.push(...(data || []));
        if (!data || data.length < LOTE_CSV) break;
      }

      if (vistaEvaluaciones) {
        descargarCSV(
          ['Nombre', 'Email', 'Estado', 'Fecha', 'Puntuación', 'Nivel', ...COMPETENCIAS_CSV.map(([, label]) => label)],
          todas.map(({ evaluacion: e, nombre, email }) => [
            nombre,
            email,
            e.completada ? 'Completada' : 'Inconclusa',
            formatearFecha(e.fecha_evaluacion),
            e.completada ? Math.round(e.puntuacion_total) : '',
            e.completada ? calcularNivelLiderazgo(e.puntuacion_total) : '',
            ...COMPETENCIAS_CSV.map(([key]) => e[key]),
          ]),
          `evaluaciones_${vista}`
        );
      } else {
        descargarCSV(
          ['Nombre', 'Email', 'Rol', 'Registrado', 'Último acceso',
            'Evaluaciones completadas', 'Evaluaciones totales', 'Última puntuación', 'Nivel'],
          todas.map((u) => [
            u.nombre,
            u.email,
            ETIQUETAS_ROL[u.rol] || u.rol,
            formatearFecha(u.registrado_en),
            formatearFecha(u.ultimo_acceso),
            u.completadas,
            u.total_evaluaciones,
            u.ultima_puntuacion != null ? Math.round(u.ultima_puntuacion) : '',
            calcularNivelLiderazgo(u.ultima_puntuacion),
          ]),
          'usuarios'
        );
      }
    } catch (err) {
      console.error('Error al exportar CSV:', err);
      window.alert('No se pudo exportar el CSV.');
    } finally {
      setExportando(false);
    }
  };

  const renderTablaUsuarios = () => (
    <table className="tabla-evaluaciones tabla-admin">
      <thead>
        <tr>
          <ThOrdenable campo="nombre" orden={orden} onOrdenar={ordenarPor}>Usuario</ThOrdenable>
          <ThOrdenable campo="rol" orden={orden} onOrdenar={ordenarPor}>Rol</ThOrdenable>
          <ThOrdenable campo="registrado_en" orden={orden} onOrdenar={ordenarPor}>Registrado</ThOrdenable>
          <ThOrdenable campo="ultimo_acceso" orden={orden} onOrdenar={ordenarPor}>Último acceso</ThOrdenable>
          <ThOrdenable campo="evaluaciones" orden={orden} onOrdenar={ordenarPor}>Evaluaciones</ThOrdenable>
          <ThOrdenable campo="ultima_puntuacion" orden={orden} onOrdenar={ordenarPor}>Última puntuación</ThOrdenable>
          {esSuperadmin && <th>Acciones</th>}
        </tr>
      </thead>
      <tbody>
        {filas.map((u) => (
          <tr key={u.id} className="fila-clickable" onClick={() => navigate(`/admin/usuarios/${u.id}`)}>
            <td data-label="Usuario"><PersonaCelda imagen={u.imagen} nombre={u.nombre} email={u.email} /></td>
            <td data-label="Rol">
              <span className={`badge-rol badge-rol-${u.rol}`}>{ETIQUETAS_ROL[u.rol] || u.rol}</span>
            </td>
            <td data-label="Registrado">{formatearFecha(u.registrado_en)}</td>
            <td data-label="Último acceso">{formatearFecha(u.ultimo_acceso)}</td>
            <td data-label="Evaluaciones">{u.completadas} / {u.total_evaluaciones}</td>
            <td data-label="Última puntuación">
              {u.ultima_puntuacion != null ? (
                <>
                  <strong>{Math.round(u.ultima_puntuacion)}</strong>
                  <div className="admin-nivel">{calcularNivelLiderazgo(u.ultima_puntuacion)}</div>
                </>
              ) : 'Sin evaluar'}
            </td>
            {esSuperadmin && (
              <td data-label="Acciones">
                {u.id !== usuarioAuth?.id && u.rol !== 'superadmin' ? (
                  <div className="acciones-buttons">
                    <BotonIcono {...iconoRol(u.rol)} onClick={(e) => onAlternarAdmin(e, u)} />
                    <BotonIcono icono="🗑️" texto="Eliminar usuario" peligro onClick={(e) => onEliminarUsuario(e, u)} />
                  </div>
                ) : '—'}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderTablaEvaluaciones = () => (
    <table className="tabla-evaluaciones tabla-admin">
      <thead>
        <tr>
          <ThOrdenable campo="persona" orden={orden} onOrdenar={ordenarPor}>Persona</ThOrdenable>
          <ThOrdenable campo="resultado" orden={orden} onOrdenar={ordenarPor}>Resultado</ThOrdenable>
          {/* El nivel sale de la puntuación, así que ordena igual que Resultado */}
          <ThOrdenable campo="resultado" orden={orden} onOrdenar={ordenarPor}>Nivel</ThOrdenable>
          <ThOrdenable campo="fecha" orden={orden} onOrdenar={ordenarPor}>Fecha</ThOrdenable>
          <th>Acciones</th>
        </tr>
      </thead>
      <tbody>
        {filas.map(({ evaluacion: e, usuario_id, nombre, email, imagen }) => (
          <tr key={e.id} className={!e.completada ? 'evaluacion-inconclusa-row' : ''}>
            <td data-label="Persona"><PersonaCelda imagen={imagen} nombre={nombre} email={email} /></td>
            <td data-label="Resultado">
              {e.completada ? <strong>{Math.round(e.puntuacion_total)}</strong> : 'Sin completar'}
            </td>
            <td data-label="Nivel">
              {e.completada ? calcularNivelLiderazgo(e.puntuacion_total) : '—'}
            </td>
            <td data-label="Fecha">{formatearFecha(e.fecha_evaluacion)}</td>
            <td data-label="Acciones">
              <div className="acciones-buttons">
                <BotonIcono icono="👁️" texto="Ver respuestas" onClick={() => setEvaluacionSeleccionada({ evaluacion: e, nombre })} />
                <BotonIcono icono="👤" texto="Ir al perfil" onClick={() => navigate(`/admin/usuarios/${usuario_id}`)} />
                {esSuperadmin && (
                  <BotonIcono icono="🗑️" texto="Eliminar evaluación" peligro onClick={() => onEliminarEvaluacion(e, nombre)} />
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="admin-container">
      <main className="admin-main">
        <div className="admin-header">
          <h1>Panel de administración</h1>
          <button className="btn-volver" onClick={() => navigate('/')}>
            ← Volver al Dashboard
          </button>
        </div>

        <div className="admin-stats">
          <button
            type="button"
            className={`admin-stat ${vista === 'usuarios' ? 'activa' : ''}`}
            onClick={verTodos}
          >
            <span className="admin-stat-valor">{resumen.registrados}</span>
            <span className="admin-stat-label">Registrados</span>
          </button>
          <button
            type="button"
            className={`admin-stat ${vista === 'completadas' ? 'activa' : ''}`}
            onClick={() => alternarVista('completadas')}
            aria-pressed={vista === 'completadas'}
          >
            <span className="admin-stat-valor">{resumen.completadas}</span>
            <span className="admin-stat-label">Evaluaciones completadas</span>
          </button>
          <button
            type="button"
            className={`admin-stat ${vista === 'inconclusas' ? 'activa' : ''}`}
            onClick={() => alternarVista('inconclusas')}
            aria-pressed={vista === 'inconclusas'}
          >
            <span className="admin-stat-valor">{resumen.inconclusas}</span>
            <span className="admin-stat-label">Evaluaciones inconclusas</span>
          </button>
        </div>

        <div className="admin-filtros">
          <input
            type="search"
            className="admin-busqueda"
            placeholder="Buscar por nombre o email"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
          <select
            className="admin-select"
            value={filtroRol}
            onChange={(e) => { setFiltroRol(e.target.value); setPagina(1); }}
          >
            <option value="todos">Todos los roles</option>
            <option value="usuario">Usuarios</option>
            <option value="admin">Admins</option>
            <option value="superadmin">Superadmin</option>
          </select>
          <button className="btn-volver" onClick={exportarCSV} disabled={total === 0 || exportando}>
            {exportando ? 'Exportando...' : 'Exportar CSV'}
          </button>
        </div>

        {error ? (
          <div className="admin-vacio admin-error">{error}</div>
        ) : filas.length === 0 ? (
          <div className="admin-vacio">
            {cargando
              ? 'Cargando...'
              : vistaEvaluaciones ? 'No hay evaluaciones que coincidan' : 'No hay usuarios que coincidan'}
          </div>
        ) : (
          <>
            {/* Al cambiar de página se mantiene la anterior atenuada hasta que llegue la nueva */}
            <div className={`tabla-container ${cargando ? 'cargando' : ''}`}>
              {vistaEvaluaciones ? renderTablaEvaluaciones() : renderTablaUsuarios()}
            </div>
            <Paginacion pagina={pagina} total={total} onCambiar={setPagina} />
          </>
        )}
      </main>

      {evaluacionSeleccionada && (
        <DetalleEvaluacionModal
          usuario={evaluacionSeleccionada.nombre}
          evaluacion={evaluacionSeleccionada.evaluacion}
          onCerrar={() => setEvaluacionSeleccionada(null)}
        />
      )}
    </div>
  );
};

export default AdminPanel;
